"use client";

import { ArrowRight, TrendingUp } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Rateparityhero() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const trustSignals = [{
    label: "Revenue Growth"
  }, {
    label: "Data-Driven"
  }, {
    label: "OTA Experts"
  }, {
    label: "Tech-Forward"
  }];
  const cityPairs = [{
    from: "OTA",
    to: "DIR"
  }, {
    from: "REV",
    to: "AUD"
  }, {
    from: "TECH",
    to: "IMP"
  }, {
    from: "DATA",
    to: "STR"
  }, {
    from: "MKT",
    to: "REV"
  }, {
    from: "AUD",
    to: "OPT"
  }];
  return <section data-section-id="824" id="rateparityhero" className="relative bg-background text-foreground overflow-hidden min-h-[720px] lg:h-screen flex items-stretch">
      <div className="grid grid-cols-1 lg:grid-cols-12 w-full">
        {}
        <div ref={leftRef} className={`lg:col-span-7 relative flex flex-col justify-between px-6 py-12 sm:px-10 sm:py-16 lg:px-20 lg:py-20 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          {}
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <span className="font-mono text-xs sm:text-sm tracking-widest uppercase text-foreground/80 flex items-center gap-2">
                <span className="inline-block w-1.5 h-1.5 rounded-lg bg-secondary" />
                HOTELREVBABA / EST. 2024 / GLOBAL REACH
              </span>
            </div>
            <div className="h-px w-24 bg-border" />
          </div>

          {}
          <div className="mt-10 lg:mt-12">
            <h1 className="font-default font-extrabold text-foreground leading-[0.92]" style={{
            fontSize: "clamp(3rem, 9vw, 9rem)",
            letterSpacing: "-0.045em"
          }}>
              <span className="block">Maximize your</span>
              <span className="block">hotel revenue</span>
              <span className="block relative inline-block">
                <span className="relative inline-block">
                  today.
                  <svg className="absolute left-0 -bottom-2 w-full" viewBox="0 0 300 24" fill="none" preserveAspectRatio="none" aria-hidden="true">
                    <path d="M2 16 C 60 4, 140 22, 220 10 S 290 14, 298 8" stroke="var(--secondary)" strokeWidth="5" strokeLinecap="round" fill="none" style={{
                    strokeDasharray: 600,
                    strokeDashoffset: leftVisible ? 0 : 600,
                    transition: "stroke-dashoffset 1.4s ease-out 0.6s"
                  }} />
                  </svg>
                </span>
              </span>
            </h1>

            <p className="mt-8 max-w-[480px] text-base sm:text-lg text-foreground/70 leading-relaxed">
              Unlock your property's full potential with data-driven OTA strategies, expert tech stack implementation, and comprehensive revenue audits by Sunil Singh.
            </p>

            <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
              <Link to="/BlogPage" className="text-sm font-medium text-secondary hover:underline flex items-center gap-1">
                Read Related Blog Post <ArrowRight className="size-3" />
              </Link>
              <Link to="/OurServicesPage" className="text-sm font-medium text-secondary hover:underline flex items-center gap-1">
                Explore Other Services <ArrowRight className="size-3" />
              </Link>
              <Link to="/ResourcesPage" className="text-sm font-medium text-secondary hover:underline flex items-center gap-1">
                Download Free Checklist <ArrowRight className="size-3" />
              </Link>
              <Link to="/ContactPage" className="text-sm font-medium text-secondary hover:underline flex items-center gap-1">
                Schedule Consultation <ArrowRight className="size-3" />
              </Link>
            </div>

            {}
            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="rounded-lg px-7 h-12 text-base font-semibold bg-secondary text-primary-foreground hover:bg-secondary/90 shadow-md">
                <Link to="/ContactPage">
                  Book a Consultation
                  <ArrowRight className="ml-1 size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg" className="rounded-lg px-7 h-12 text-base font-semibold border-2 border-foreground text-foreground bg-transparent hover:bg-foreground hover:text-background">
                <Link to="/OurServicesPage">View Our Services</Link>
              </Button>
            </div>
          </div>

          {}
          <div className="mt-12 lg:mt-16 pt-6 border-t border-border">
            <div className="flex items-center gap-3 sm:gap-5 overflow-x-auto whitespace-nowrap font-mono text-xs sm:text-sm uppercase tracking-wider text-foreground/70 -mx-2 px-2">
              {trustSignals.map((item, i) => <div key={i} data-index={i} className="flex items-center gap-3 sm:gap-5 shrink-0">
                  {i > 0 && <span className="w-1.5 h-1.5 rounded-lg bg-secondary" />}
                  <span>{item.label}</span>
                </div>)}
            </div>
          </div>
        </div>

        {}
        <div ref={rightRef} className={`lg:col-span-5 relative min-h-[70vh] lg:min-h-0 transition-all duration-1000 ease-out ${rightVisible ? "opacity-100 translate-x-0" : "opacity-0 translate-x-6"}`}>
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_management_dashboard.png" alt="Professional hotel revenue management dashboard showing growth analytics" loading="lazy" decoding="async" className="absolute inset-0 w-full h-full object-cover" />

          {}
          <div className="absolute bottom-6 left-6 sm:bottom-10 sm:left-10 max-w-[300px] bg-foreground text-background p-5 rounded-lg shadow-xl">
            <div className="flex items-center gap-2 mb-3">
              <TrendingUp className="size-4 text-secondary" />
              <span className="font-mono text-[10px] tracking-widest uppercase text-background/60">
                Live Insights
              </span>
            </div>

            {}
            <div className="flex items-center gap-2 mb-3">
              <span className="w-2 h-2 rounded-lg bg-background" />
              <div className="flex-1 h-px bg-background/40 relative">
                <div className="absolute inset-0 h-px bg-secondary origin-left animate-pulse" />
              </div>
              <span className="w-2 h-2 rounded-lg bg-secondary" />
            </div>

            <p className="font-mono text-sm sm:text-base tracking-tight text-background mb-3">
              OTA OPTIMIZATION → DIRECT BOOKING GROWTH
            </p>

            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-lg bg-background/10 border border-background/20">
              <span className="relative flex size-2">
                <span className="absolute inline-flex h-full w-full rounded-lg bg-secondary opacity-75 animate-ping" />
                <span className="relative inline-flex rounded-lg size-2 bg-secondary" />
              </span>
              <span className="font-mono text-[10px] tracking-widest uppercase text-background">
                Active Strategy
              </span>
            </div>
          </div>

          {}
          <div className="hidden lg:flex absolute top-0 right-0 h-full w-10 bg-foreground items-center justify-center overflow-hidden">
            <div className="flex flex-col gap-6 font-mono text-[11px] tracking-widest uppercase text-background/80" style={{
            writingMode: "vertical-rl",
            animation: "haulx-ticker 30s linear infinite"
          }}>
              {[...cityPairs, ...cityPairs].map((pair, i) => <span key={i} data-index={i} className="whitespace-nowrap">
                  {pair.from} → {pair.to}
                </span>)}
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes haulx-ticker {
          0% { transform: translateY(-50%); }
          100% { transform: translateY(50%); }
        }
      `}</style>

      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [{
          "@type": "ListItem",
          "position": 1,
          "name": "Home",
          "item": "https://hotelrevbaba.com/HomePage"
        }, {
          "@type": "ListItem",
          "position": 2,
          "name": "Services",
          "item": "https://hotelrevbaba.com/OurServicesPage"
        }, {
          "@type": "ListItem",
          "position": 3,
          "name": "Rate Parity Leakage Control",
          "item": "https://hotelrevbaba.com/RateParityLeakageControlPage"
        }]
      })
    }} />
    </section>;
}